import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import {
  ApplicationCommandOptionType as T,
  ChannelType,
  type ApplicationCommand,
  type CommandOption,
} from '@comms/shared';
import { displayName, useStore } from '../store';
import { dismissNotice, useCommands, type InteractionNotice } from '../store/commands';
import { Avatar, Button, Icon, cx } from './ui';
import { Markdown } from './Markdown';
import { RichEmbed } from './Embed';

/** Commands matching what's typed after "/", shown above the composer. */
export function CommandPicker({
  matches,
  index,
  onPick,
}: {
  matches: ApplicationCommand[];
  index: number;
  onPick: (c: ApplicationCommand) => void;
}) {
  const users = useStore((s) => s.users);
  return (
    <div
      role="listbox"
      aria-label="Commands"
      className="absolute bottom-full left-0 right-0 mb-2 max-h-80 overflow-y-auto rounded bg-bg-1 p-1 shadow-xl"
    >
      {matches.map((c, i) => (
        <button
          key={c.id}
          type="button"
          role="option"
          aria-selected={i === index}
          onMouseDown={(e) => (e.preventDefault(), onPick(c))}
          className={cx(
            'flex w-full items-center gap-3 rounded px-2 py-1.5 text-left text-sm',
            i === index ? 'bg-bg-active text-text-0' : 'text-text-2',
          )}
        >
          <Avatar userId={c.applicationId} size={20} />
          <span className="font-semibold text-text-0">/{c.name}</span>
          {c.options.map((o) => (
            <span key={o.name} className="text-xs text-text-3">
              {o.required ? o.name : `[${o.name}]`}
            </span>
          ))}
          <span className="flex-1 truncate text-text-3">{c.description}</span>
          <span className="text-xs text-text-3">
            {users[c.applicationId]?.displayName ?? 'bot'}
          </span>
        </button>
      ))}
    </div>
  );
}

/** Option fields for the chosen command; Enter runs it, Escape cancels. */
export function CommandForm({
  cmd,
  guildId,
  onCancel,
  onRun,
}: {
  cmd: ApplicationCommand;
  guildId: string;
  onCancel: () => void;
  onRun: (options: Record<string, string>) => Promise<void>;
}) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const first = useRef<HTMLInputElement & HTMLSelectElement>(null);
  const botName = useStore((s) => displayName(s, cmd.applicationId, guildId));
  const runButton = useRef<HTMLButtonElement>(null);
  const running = useRef(false);
  useEffect(() => (first.current ?? runButton.current)?.focus(), [cmd.id]);

  const missing = cmd.options.some((o) => o.required && !values[o.name]?.trim());
  async function run() {
    if (missing || running.current) return;
    running.current = true;
    setBusy(true);
    try {
      const options = Object.fromEntries(Object.entries(values).filter(([, v]) => v.trim() !== ''));
      await onRun(options);
    } finally {
      running.current = false;
      setBusy(false);
    }
  }
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void run();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
  };

  return (
    <div className="flex flex-1 flex-wrap items-center gap-2" onKeyDown={onKeyDown}>
      <span
        className="rounded bg-accent/20 px-1.5 py-0.5 text-sm font-semibold text-text-0"
        title={`${cmd.description} · ${botName}`}
      >
        /{cmd.name}
      </span>
      {cmd.options.length === 0 && (
        <span className="text-sm text-text-3">{cmd.description} · press Enter</span>
      )}
      {cmd.options.map((o, i) => (
        <label key={o.name} className="flex items-center gap-1 rounded bg-bg-1 px-2 py-0.5 text-sm">
          <span className="text-text-2">
            {o.name}
            {o.required ? '' : '?'}
          </span>
          <OptionField
            option={o}
            guildId={guildId}
            value={values[o.name] ?? ''}
            onChange={(v) => setValues((cur) => ({ ...cur, [o.name]: v }))}
            inputRef={i === 0 ? first : undefined}
          />
        </label>
      ))}
      <span className="flex-1" />
      <Button variant="ghost" onClick={onCancel}>
        Cancel
      </Button>
      <Button ref={runButton} disabled={missing || busy} onClick={() => void run()}>
        Run
      </Button>
    </div>
  );
}

function OptionField({
  option,
  guildId,
  value,
  onChange,
  inputRef,
}: {
  option: CommandOption;
  guildId: string;
  value: string;
  onChange: (v: string) => void;
  inputRef?: React.Ref<HTMLInputElement & HTMLSelectElement>;
}) {
  const members = useStore((s) => s.members[guildId]);
  const channels = useStore((s) => s.channels);
  const roles = useStore((s) => s.guilds[guildId]?.roles);
  const state = useStore.getState();
  const choices: { id: string; label: string }[] =
    option.type === T.USER
      ? Object.values(members ?? {}).map((m) => ({
          id: m.user.id,
          label: displayName(state, m.user.id, guildId),
        }))
      : option.type === T.CHANNEL
        ? Object.values(channels)
            .filter((c) => c.guildId === guildId && c.type !== ChannelType.GUILD_CATEGORY)
            .map((c) => ({ id: c.id, label: `#${c.name}` }))
        : option.type === T.ROLE
          ? (roles ?? [])
              .filter((r) => r.id !== guildId)
              .map((r) => ({ id: r.id, label: `@${r.name}` }))
          : option.type === T.BOOLEAN
            ? [
                { id: 'true', label: 'yes' },
                { id: 'false', label: 'no' },
              ]
            : [];
  const common = {
    ref: inputRef,
    'aria-label': option.name,
    title: option.description,
    value,
    className: 'min-w-0 bg-transparent text-text-0 outline-none',
  };
  if (option.type !== T.STRING && option.type !== T.INTEGER) {
    const list =
      option.type === T.BOOLEAN ? choices : choices.sort((a, b) => a.label.localeCompare(b.label));
    return (
      <select {...common} onChange={(e) => onChange(e.target.value)}>
        <option value="">…</option>
        {list.map((c) => (
          <option key={c.id} value={c.id}>
            {c.label}
          </option>
        ))}
      </select>
    );
  }
  return (
    <input
      {...common}
      type={option.type === T.INTEGER ? 'number' : 'text'}
      step={option.type === T.INTEGER ? 1 : undefined}
      placeholder={option.description}
      maxLength={1000}
      size={Math.max(8, Math.min(30, value.length + 2))}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

const EMPTY: never[] = [];

/** Your commands in this channel that are waiting, failed, or answered only for you. */
export function InteractionNotices({ channelId, guildId }: { channelId: string; guildId: string }) {
  const notices = useCommands((s) => s.notices[channelId] ?? EMPTY);
  return (
    <>
      {notices.map((n) => (
        <Notice key={n.id} n={n} guildId={guildId} />
      ))}
    </>
  );
}

function Notice({ n, guildId }: { n: InteractionNotice; guildId: string }) {
  const name = useStore((s) => displayName(s, n.applicationId, guildId));
  return (
    <div data-testid="interaction-notice" className="mx-4 mt-2 flex gap-4 rounded bg-accent/5 py-1">
      <Avatar userId={n.applicationId} size={40} />
      <div className="min-w-0 flex-1">
        <div className="mb-0.5 text-xs text-text-3">
          You used <span className="font-medium text-accent">{n.label}</span>
        </div>
        <div className="flex items-baseline gap-2">
          <span className="font-medium text-text-0">{name}</span>
          <span className="rounded bg-accent px-1 text-[10px] font-bold leading-4 text-white">
            BOT
          </span>
        </div>
        {n.state === 'pending' || n.state === 'thinking' ? (
          <div className="text-sm italic text-text-3">{name} is thinking…</div>
        ) : n.state === 'failed' ? (
          <div className="text-sm text-danger">The application did not respond</div>
        ) : (
          <div className="text-text-1">
            {n.content && <Markdown content={n.content} guildId={guildId} />}
            {n.embeds?.map((e, i) => (
              <RichEmbed key={i} embed={e} guildId={guildId} />
            ))}
          </div>
        )}
        {(n.state === 'ephemeral' || n.state === 'failed') && (
          <div className="mt-1 flex items-center gap-1 text-xs text-text-3">
            <Icon name="eye" className="h-3 w-3" /> Only you can see this ·
            <button
              type="button"
              className="text-accent hover:underline"
              onClick={() => dismissNotice(n.channelId, n.id)}
            >
              Dismiss message
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
